import { useState, useEffect, useMemo } from "react";
import {
  Box,
} from "@mui/material";
import ActionItems from "../../components/ActionItems/ActionItems";
import ActionItem from "../../components/ActionItems/ActionItem";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { initialSort } from "../../components/ActionItems/actionItemTableConfig";
import { ActionItemType, UserTeamData } from "../../utils/types";
import useGlobalContext from "../../context/useGlobalContext";
import ActionItemService from "../../services/actionItems/ActionItemService";
import { getStatusOptions, getUniqueAssignees, PRIORITY_OPTIONS } from "../../utils/utils";
import SlideOver from "../../components/Common/SlideOver";
import UserInteractionsService from "../../services/interactions/Interactions";
import FilterPill from "../../components/Common/FilterPill";
import { Options } from "../../utils/types";

const ClientActionItem: React.FC<{
  clientDomain?: string;
  loggedInEmail?: string;
  selectedUserEmail?: string;
  newUserTeamData?: UserTeamData | null;
  userEmail?: string;
  parent_page_identifier?: string;
  clientOrgId?: string;
  subOrgId?: string;
  profileOrgId?: string;
}> = ({
  newUserTeamData,
  parent_page_identifier,
  clientOrgId,
  subOrgId,
  profileOrgId,
}) => {
  const { startEndMeetingTime, accountDetails } = useGlobalContext()
  const { showSnackbar } = useDisplaySnackbar();
  const [attendees, setAttendees] = useState<any[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [actionItems, setActionItems] = useState<any[]>([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [selectedActionItem, setSelectedActionItem] = useState<any | null>(null);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize,setPageSize] = useState(50);
  const [sort, setSort] = useState(initialSort);
  const [actionItemType, setActionItemType] = useState(ActionItemType.All);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [columnFilters, setColumnFilters] = useState({
    assignee: [] as string[],
    coach: [] as string[],
    dueDate: "",
    priority: [] as string[],
    account: [] as string[],
    actionItem: "",
    status: [] as string[],
    description: "",
    tags: [] as string[],
    sponsor: [] as string[],
    subscribers: [] as { id: string; entityType?: "user" | "space" }[],
    actions: "",
  });
  const loggedInProfileOrgId = accountDetails?.profileOrgId ?? "";
  const [activeUsers, setActiveUsers] = useState<any[]>([]);
  const toStringArray = (value: any): string[] | undefined => {
    if (value === undefined || value === null || value === "") return undefined;
    return Array.isArray(value) ? value : [value];
  };
  const tagsList = useMemo(
    () =>
      actionItems.flatMap((item: any) =>
        Array.isArray(item?.tags) ? item.tags : []
      ),
    [actionItems]
  );

  const {
    isSuccess,
    isLoading: isActiveUsersLoading,
    isFetching: isActiveUsersFetching,
    isError,
    error,
    data: activeUsersData,
  } = UserInteractionsService.useGetActiveUsers({}, true);

  useEffect(() => {
    if (isSuccess) {
      const activeUsers = activeUsersData?.data?.data || [];
      setActiveUsers(activeUsers);
    }
    if (isError) {
      showSnackbar("error", `Error fetching active users: ${error}`);
    }
  }, [
    isError,
    error,
    activeUsersData,
    isSuccess,
    isActiveUsersLoading,
    isActiveUsersFetching,
  ]);
  
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);

  const isFromContacts = parent_page_identifier === "contacts";
  const effectiveAssigneePorIds = isFromContacts
    ? (columnFilters.assignee.length ? toStringArray(columnFilters.assignee) : toStringArray(profileOrgId))
    : toStringArray(
        columnFilters.assignee.length ? columnFilters.assignee : undefined
      );

  const {
    isLoading: isMyActionsLoading,
    isFetching: isMyActionsFetching,
    isSuccess: isMyActionsDataSuccess,
    isError: isMyActionsDataError,
    error: myActionsDataError,
    data: myActionsData,
    } = ActionItemService.useGetPageActionItems(
    {
      employeePorId: loggedInProfileOrgId ||"",
      offset: pageNumber,
      limit: pageSize,
      startTime: "",
      endTime: "",
      sortOrder: sort.sort_order,
      sortBy: sort.sort_field,
      isForTeam: false,
      actionItem: columnFilters.description,
      assigneePorIds: effectiveAssigneePorIds,
      priority: columnFilters.priority.length ? columnFilters.priority.join(",") : "",
      status: columnFilters.status.length ? columnFilters.status.join(",") : "",
      clientOrgId: subOrgId,
      isTaskFromFingertipMetrics: isFromContacts ? false : true,
      createdAtStartTime: startEndMeetingTime?.start_time,
      createdAtEndTime: todayEnd.toISOString(),
    },
    true
  );

  const {
    isLoading: isActionItemCountLoading,
    isFetching: isActionItemCountFetching,
    isSuccess: isActionItemCountSuccess,
    isError: isActionItemCountError,
    error: actionItemCountError,
    data: actionItemCountData,
  } = ActionItemService.useGetActionItemCount(
    {
      employeePorId: loggedInProfileOrgId ||"",
      startTime: "",
      endTime: "",
      isForTeam: false,
      actionItem: columnFilters.description,
      assigneePorIds: effectiveAssigneePorIds,
      priority: columnFilters.priority.length ? columnFilters.priority.join(",") : "",
      status: columnFilters.status.length ? columnFilters.status.join(",") : "",
      clientOrgId: clientOrgId,
      isTaskFromFingertipMetrics: isFromContacts ? false : true,
      subOrgId: subOrgId,
      createdAtStartTime: startEndMeetingTime?.start_time,
      createdAtEndTime: todayEnd.toISOString(),
    },
    true
  );

  useEffect(() => {
    if (isActionItemCountSuccess && actionItemCountData) {
      setTotalRecord(actionItemCountData?.count || 0);
    }
    if (isActionItemCountError) {
      showSnackbar("error", actionItemCountError?.message);
    }
  }, [isActionItemCountSuccess, isActionItemCountError, actionItemCountData, actionItemCountError,]);

    useEffect(() => {
        setShowSpinner(isMyActionsLoading || isMyActionsFetching);
        if (isMyActionsDataSuccess) {
        const myActionItemList = myActionsData?.data?.data;
        setActionItems(myActionItemList);
        }
        if (isMyActionsDataError) {
        showSnackbar("error", myActionsDataError);
        }
    }, [
        isMyActionsLoading,
        isMyActionsFetching,
        isMyActionsDataSuccess,
        isMyActionsDataError,
    ]);
  const onPageChange = (value: number) => {
    setPageNumber(value);
  };


  const onFilter = () => {
    setPageNumber(1);
  };

  const onTypeChange = (value: ActionItemType) => {
    setActionItemType(value);
    setPageNumber(1);
  };

  const handleActionItemSelection = (actionItem: any) => {
    setOpenDialog(true);
    setSelectedActionItem(actionItem);
    setAttendees([actionItem?.assignee]);
  };

  const handleActionItemPreview = (actionItem: any) => {
    setOpenDialog(true);
    setSelectedActionItem(actionItem);
    setAttendees([actionItem?.assignee]);
    setIsPreview(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedActionItem(null);
    setIsPreview(false);
  };

  const updateColumnFilter = (columnKey: string, value: any) => {
    setPageNumber(1);
    setColumnFilters((prev) => ({
      ...prev,
      [columnKey]:
        columnKey === "description"
          ? value || ""
          : Array.isArray(value)
          ? value
          : value
          ? [value]
          : [],
    }));
  };

  const peopleOptions = useMemo(
    () =>
      getUniqueAssignees({ usersList: activeUsers }).map((option) => ({
        ...option,
        type: "assignee" as const,
      })),
    [activeUsers]
  );
  const statusOptions = getStatusOptions();
  type VisibleFilterKey = "assignee" | "priority" | "status" | "description";
  const filterOrder: VisibleFilterKey[] = [
    "assignee",
    "priority",
    "status",
    "description",
  ];

  const columnVisibility: Record<string, boolean> = {
    serialnumber: true,
    description: true,
    account: false,
    assignee: true,
    coach: false,
    dueDate: true,
    priority: true,
    status: true,
    columnFilter: true,
    momlink: false,
    sponsor: false,
    tags: false,
    subscribers: false,
    createdAt: false,
    actions: true,
  };

  const [columnOrder, setColumnOrder] = useState([
    "serialnumber",
    "description",
    "assignee",
    "dueDate",
    "priority",
    "status",
    "actions",
  ]);

  return (
    <>
      <Box
        id="actionItemsParent"
        className="flex flex-col justify-start items-start gap-6"
        style={{ marginTop: "-10px" }}
      >
        <Box className="actionItemsContent w-full">
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              marginBottom: "12px",
              marginTop: "6px",
            }}
          >
            {filterOrder.map((key) => {
              const value = columnFilters[key];

              if (!value || (Array.isArray(value) && value.length === 0)) {
                return null;
              }

              let options: Options[] = [];
              let displayValue = "";
              let avatar: string | undefined;
              let email: string | undefined;

              switch (key) {
                case "assignee": {
                  const selectedValues = Array.isArray(value) ? value : [];
                  options = peopleOptions.map((option) => ({
                    ...option,
                    type: key as "assignee",
                    email: option.email,
                  }));

                  if (selectedValues.length > 0) {
                    displayValue = selectedValues
                      .map((selectedValue: string) => options.find((o) => o.value === selectedValue)?.label || selectedValue)
                      .join(", ");
                    const firstSelected = options.find((o) => o.value === selectedValues[0]);
                    avatar = firstSelected?.avatar;
                    email = firstSelected?.email;
                  }
                  break;
                }
                case "priority": {
                  const selectedValues = Array.isArray(value) ? value : [];
                  options = PRIORITY_OPTIONS;
                  displayValue = selectedValues
                    .map((selectedValue: string) => options.find((o) => o.value === selectedValue)?.label || selectedValue)
                    .join(", ");
                  break;
                }
                case "status": {
                  const selectedValues = Array.isArray(value) ? value : [];
                  options = statusOptions;
                  displayValue = selectedValues
                    .map((selectedValue: string) => options.find((o) => o.value === selectedValue)?.label || selectedValue)
                    .join(", ");
                  break;
                }
                case "description":
                  displayValue = value as string;
                  break;
                default:
                  displayValue = Array.isArray(value) ? value.join(", ") : value;
              }

              return (
                <FilterPill
                  key={key}
                  label={key}
                  options={options}
                  type={key === "description" ? "text" : "select"}
                  value={key === "description" ? (value as string) : (Array.isArray(value) ? value : [])}
                  displayValue={displayValue}
                  avatar={avatar}
                  email={email}
                  onChange={(selectedValue) => {
                    if (key === "description") {
                      updateColumnFilter("description", selectedValue || "");
                      return;
                    }

                    if (Array.isArray(selectedValue)) {
                      updateColumnFilter(key, selectedValue);
                    } else {
                      updateColumnFilter(key, selectedValue ? [selectedValue] : []);
                    }
                  }}
                />
              );
            })}
          </Box>
          <Box sx={{ marginTop: "10px" }}>
            <ActionItems
              type={parent_page_identifier === "contacts" ? "MY_ACTION_ITEMS" : "TEAM_ACTION_ITEMS"}
              isLoading={isMyActionsLoading}
              actionItems={actionItems}
              totalRecord={totalRecord}
              handleActionItemSelection={handleActionItemSelection}
              handleActionItemPreview={handleActionItemPreview}
              pageSize={pageSize}
              onPageChange={onPageChange}
              onFilter={onFilter}
              actionItemType={actionItemType}
              onTypeChange={onTypeChange}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              columnFilters={columnFilters}
              updateColumnFilter={updateColumnFilter}
              sort={sort}
              onSortChange={(columnKey, direction) =>
                setSort({
                  sort_field: columnKey,
                  sort_order: direction,
                })
              }
              newUserTeamData={newUserTeamData} bulkUpdateValues={undefined}
              columnVisibility={columnVisibility}
              enableRowSelection={false}
              activeUsers={activeUsers}
              tagsList={tagsList}
              showSpinner={showSpinner}
              isCountLoading={isActionItemCountLoading || isActionItemCountFetching}
              feedbackEnabled={true}
              state={{ columnOrder }}
              onColumnOrderChange={setColumnOrder}
              tableMinHeight= "calc(100vh - 325px)"
            />
          </Box>
        </Box>
      </Box>

      <SlideOver isOpen={openDialog} onClose={handleClose} closeOnRight>
        <Box>
          <ActionItem
            actionItemType={parent_page_identifier === "contacts" ? "MY_ACTION_ITEMS":"TEAM_ACTION_ITEMS"}
            attendees={attendees}
            meetingId={selectedActionItem?.interactionId}
            selectedActionItem={selectedActionItem}
            handleClose={handleClose}
            isPreview={isPreview}
            is_team_meeting_id={true}
            openDialog={openDialog}
            setOpenDialog={setOpenDialog}
          />
        </Box>
      </SlideOver>
    </>
  );
};


export default ClientActionItem;
